import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import UiComponentView from 'inquiry/components/UiComponentView';
import { isFinalFlowStep } from 'inquiry/lib/inquiry';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { TransitionParams } from 'inquiry/lib/types';
import { UiStatus } from 'inquiry/redux/actions/ui';
import InquiryCompleteView from 'inquiry/views/InquiryCompleteView';
import { Step, StepCompleteConfig, StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import { UiComponent } from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { flowHasCompleteAction } from 'verify/lib/inquiry';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry, PollingMode } from 'verify/models/inquiry';
import { InquirySession } from 'verify/models/inquiry-session';
import { RootState } from 'verify/redux/store';

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

interface InquiryUiViewProps {
  inquiry: Inquiry;
  /**
   * For embedded UI steps
   */
  nextStep?: Step;
  currentSession?: InquirySession;
  uiStatus: UiStatus;
  handleLanguageChange: UiComponentActionsApi['handleLanguageChange'];
  showMessage: UiComponentActionsApi['showMessage'];
  onTransitionSubmit: (values: TransitionParams, componentName: string) => Promise<void>;
  onTransitionAction: (componentName: string) => Promise<void>;
  onComplete: () => void;
  onCancel: () => void;
  route: (path: string, state?: Record<string, any>) => void;
  setComponent: (component: UiComponent) => void;
  featureFlags?: FeatureFlagsMap;
}

const InquiryUiView = memo(
  ({
    inquiry,
    nextStep = inquiry.attributes['next-step'],
    currentSession,
    handleLanguageChange,
    showMessage,
    onTransitionSubmit,
    onTransitionAction,
    onComplete,
    onCancel,
    route,
    setComponent,
  }: InquiryUiViewProps) => {
    const isChildFlow = currentSession?.relationships['parent-session'].data != null;
    const [t] = useTranslation();

    switch (nextStep.type) {
      case UiStepType.Ui: {
        if (
          isFinalFlowStep(inquiry) &&
          currentSession?.attributes['show-return-to-original-device-screen']
        ) {
          // Currently the configs don't match, so it will use the default localizations
          // TODO: (eileen) add complete localizations to device handoff config if we launch this
          const completeConfig: StepCompleteConfig = {
            localizations: {
              ...nextStep.config.localizations,
              'child-flow-page': {
                title: t('success.title-exchange-flow'),
                prompt: t('success.prompt-exchange-flow'),
              },
              ...nextStep.config,
            },
          };
          return (
            <InquiryCompleteView
              config={completeConfig}
              onComplete={onComplete}
              hasCompleteAction={flowHasCompleteAction(inquiry)}
              isChildFlow={isChildFlow}
            />
          );
        }

        return (
          <UiComponentsForm
            // We namespace each form by step name to avoid having different steps
            // affect other step forms during the transition animation.
            inquiry={inquiry}
            nextStep={nextStep}
            // for redux-form
            form={nextStep.name}
            onTransitionSubmit={onTransitionSubmit}
            onTransitionAction={onTransitionAction}
            onComplete={onComplete}
            onCancel={onCancel}
            handleLanguageChange={handleLanguageChange}
            showMessage={showMessage}
            route={route}
            setComponent={setComponent}
          />
        );
      }
      // FIXME: These <Redirect/>s break AnimatedPage, because they cause the
      // page being transitioned away from to not render. This is because the
      // redirect causes the previous component to no longer be rendered.
      // The fix would be to actually render the combined steps directly, rather
      // than redirecting. We'll have to adjust the routing to support this.
      case UiStepType.Document:
        return <Redirect to='/inquiry/documents/prompt' />;
      case UiStepType.Integration:
        return <Redirect to='/inquiry/integration' />;
      case UiStepType.GovernmentId:
        return <Redirect to='/inquiry/government-id/select' />;
      case UiStepType.Selfie:
        return <Redirect to='/inquiry/selfie/center/prompt' />;
      case UiStepType.SelfieReflection:
        return <Redirect to='/inquiry/selfie-reflection/prompt' />;
      case UiStepType.VerifyWithPersona:
        return <Redirect to='/inquiry/verify-with-persona/passkey-authentication' />;
      case UiStepType.Complete:
        return (
          <InquiryCompleteView
            config={nextStep.config}
            onComplete={onComplete}
            hasCompleteAction={flowHasCompleteAction(inquiry)}
            isChildFlow={isChildFlow}
          />
        );
      default:
        // Can't use assertNever because we use the same interface as 2.0 inquiries.
        // TODO: Make a new Inquiry type solely for Dynamic Flow.
        captureMessage('Unsupported step type for InquiryUiView', 'warning', {
          type: nextStep.type,
        });
        HolderClient.onError({ status: 400, code: 'invalid_config' });
        return <Redirect to='/verify/invalid/config' />;
    }
  },
  skipRenderOnTransitionEnd,
);

InquiryUiView.displayName = 'InquiryUiView';
export default InquiryUiView;

/**
 * If the view is in the process of transitioning, we don't render it. This
 * fixes the next view being rendered in the previous frame when a transition
 * animation is present. We detect if we are transitioning by seeing if we
 * move from transitioning to not transitioning.
 *
 * WARNING: BE CAREFUL CHANGING THIS FUNCTION. This is hard to unit test
 * because it affects end-to-end rendering. If changing this, ensure that
 * prefilled form values work correctly, and are not mysteriously erased when
 * interacting with the page.
 */
function skipRenderOnTransitionEnd(
  prevProps: Pick<InquiryUiViewProps, 'uiStatus' | 'inquiry' | 'featureFlags'>,
  nextProps: Pick<InquiryUiViewProps, 'uiStatus' | 'inquiry' | 'featureFlags'>,
): boolean {
  // We want to force a rerender if polling completes but step doesn't
  // change, because the CONTENTS of the step may have changed.
  //
  // WARNING: We specifically do NOT want to check wasTransitioning here -- we
  // seem to sometimes incorrectly render isReady && wasTransitioning && step
  // didn't change, and rerendering here causes form values to be erased.
  //
  // WARNING: THIS IS SUBTLE. we use polling-mode instead of uiStatus because
  // the action to update inquiry state is not atomic with the action to
  // update uiStatus state. This risks rerenders happening "between" the
  // inquiry and uiStatus updates, which can lead to incorrect rerenders.
  const pollingCompletedWithoutStepChange =
    prevProps.inquiry.attributes['wait-for-transition']?.['polling-mode'] !== PollingMode.NONE &&
    nextProps.inquiry.attributes['wait-for-transition']?.['polling-mode'] === PollingMode.NONE &&
    prevProps.inquiry.attributes['next-step'].name ===
      nextProps.inquiry.attributes['next-step'].name;

  if (pollingCompletedWithoutStepChange) return false;

  const wasTransitioning = [UiStatus.Transitioning, UiStatus.Blocked].includes(prevProps.uiStatus);
  const isReady = nextProps.uiStatus === UiStatus.Ready;
  const isAnimatingOut = isReady && wasTransitioning;

  return isAnimatingOut;
}

interface UiComponentsFormProps {
  inquiry: Inquiry;
  /**
   * Just for type safety + easier refinement.
   */
  nextStep: StepUi;
  handleLanguageChange: UiComponentActionsApi['handleLanguageChange'];
  showMessage: UiComponentActionsApi['showMessage'];
  onTransitionSubmit: InquiryUiViewProps['onTransitionSubmit'];
  onTransitionAction: InquiryUiViewProps['onTransitionAction'];
  onComplete: () => void;
  onCancel: () => void;
  route: (path: string) => void;
  setComponent: (component: UiComponent) => void;
}

const UiComponentsFormView = ({
  inquiry,
  nextStep,
  handleLanguageChange,
  showMessage,
  onTransitionSubmit,
  onTransitionAction,
  onComplete,
  onCancel,
  // form callbacks
  handleSubmit,
  change,
  route,
  setComponent,
}: UiComponentsFormProps & InjectedFormProps) => {
  const { components } = nextStep.config;

  // get uiStatus here isntead of passing in from parent because react memo causes component to not update properly
  // if ui.status doesn't exist then we are outside an actual inquiry, like inquiry template mode assume inquiryLoadingState is false
  const uiStatus = useSelector<RootState, UiStatus>((state) => state.ui?.status ?? UiStatus.Ready);
  const inquiryLoading = uiStatus === UiStatus.Transitioning || uiStatus === UiStatus.Blocked;

  // Manually submit to connect name of triggering component
  const submitFormWithComponent = useCallback(
    (componentName: string, newValues: TransitionParams = {}): Promise<void> =>
      handleSubmit(async (values) => {
        for (const field in newValues) {
          change(field, newValues[field]);
        }

        // redux-form won't have new values until next render, so we manually concat them
        await onTransitionSubmit({ ...values, ...newValues }, componentName);
      })(),
    [handleSubmit, onTransitionSubmit, change],
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const actionsApi: UiComponentActionsApi = useMemo(
    () => ({
      handleLanguageChange,
      showMessage,
      setField: change,
      onSubmit: submitFormWithComponent,
      onActionButtonClick: onTransitionAction,
      onComplete,
      onCancel,
      route,
      setComponent,
    }),
    [
      handleLanguageChange,
      showMessage,
      change,
      submitFormWithComponent,
      onTransitionAction,
      onComplete,
      onCancel,
      route,
      setComponent,
    ],
  );

  return (
    <Form data-test='inquiry-ui-view__form'>
      {components.map((component) => (
        <UiComponentView
          key={component.name}
          component={component}
          inquiry={inquiry}
          inquiryLoading={inquiryLoading}
          actionsApi={actionsApi}
        />
      ))}
    </Form>
  );
};

export const UiComponentsForm = reduxForm<TransitionParams, UiComponentsFormProps>({})(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  UiComponentsFormView,
);
